import { styled } from "goober";
import { PressButton } from "./PressButton";

const DEFAULT_TAB_WIDTH = 52;

const Container = styled("div")`
  display: flex;
  align-items: center;
  height: 32px;
  background: var(--alpha4);
  border-radius: 8px;
  padding: 0 2px;
  position: relative;
  flex-shrink: 0;
`;

const Indicator = styled("div")`
  position: absolute;
  height: 28px;
  border-radius: 6px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  transition: transform var(--motion-fast) var(--ease-out);
  pointer-events: none;
`;

const Tab = styled(PressButton)`
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-family: inherit;
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
  color: var(--text-secondary);
  opacity: 0.5;
  position: relative;
  z-index: 1;
  &:hover {
    color: var(--text-primary);
    opacity: 1;
  }
  &[data-active="true"] {
    color: var(--text-primary);
    opacity: 1;
  }
`;

type Props<T extends string> = {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  tabWidth?: number;
};

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled,
  tabWidth = DEFAULT_TAB_WIDTH,
}: Props<T>) {
  const activeIndex = Math.max(0, options.indexOf(value));
  return (
    <Container>
      <Indicator
        style={{
          width: tabWidth,
          transform: `translateX(${activeIndex * tabWidth}px)`,
        }}
      />
      {options.map((option) => (
        <Tab
          key={option}
          style={{ width: tabWidth }}
          data-active={value === option}
          disabled={disabled}
          onPress={() => onChange(option)}
        >
          {option}
        </Tab>
      ))}
    </Container>
  );
}
